import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const getValidDate = (value) => {
  if (!value) return new Date();
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const SingleDatePicker = (props) => {
  const datePickerRef = useRef(null);
  const useModal = Boolean(props.useModal);
  const usePopover = Boolean(props.usePopover);
  const [date, setDate] = useState(getValidDate(props.date));

  useEffect(() => {
    if (useModal || usePopover) return undefined;

    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target) && props.setShowDatePicker) {
        props.setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [props.setShowDatePicker, useModal, usePopover]);

  useEffect(() => {
    if (props.date) {
      setDate(getValidDate(props.date));
    }
  }, [props.date]);

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const closeDatePicker = () => {
    if (props.setShowDatePicker) props.setShowDatePicker(false);
  };

  const handleFetchData = () => {
    const formattedDate = formatDate(date);
    if (props.setFrequency) props.setFrequency("Custom Date");
    if (props.onFetch) {
      props.onFetch({ startDate: formattedDate, endDate: formattedDate });
    }
    closeDatePicker();
  };

  const minDate = new Date();
  minDate.setMonth(minDate.getMonth() - 3);

  const pickerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .rdrDateDisplayWrapper {
          display: none !important;
        }
      `}</style>
      <Box sx={{
        px: 3.5, py: 2, 
        borderBottom: '1px solid #F1F5F9',
        display: 'flex', justifyContent: 'center', alignItems: 'center'
      }}>
        <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.85rem' }}>
          Select Date
        </Typography>
        {useModal && (
          <IconButton onClick={closeDatePicker} size="small" sx={{ color: "#94A3B8", p: 0.5, '&:hover': { color: '#0F172A', bgcolor: '#F1F5F9' } }}>
            <CloseIcon sx={{ height: 18, width: 18 }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Calendar
          date={date}
          onChange={item => setDate(item)}
          minDate={minDate}
          maxDate={new Date()}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3.5, py: 1.5, borderTop: '1px solid #F1F5F9' }}>
        <Button 
          sx={{ 
            background: "#0576FC", 
            color: "white", 
            textTransform: 'none', 
            px: 2.5, 
            py: 0.6, 
            borderRadius: '8px', 
            fontSize: '0.75rem', 
            fontWeight: 600, 
            boxShadow: 'none',
            '&:hover': { background: '#1D4ED8', boxShadow: 'none' } 
          }} 
          onClick={handleFetchData}
        >
          Apply Filter
        </Button>
      </Box>
    </Box>
  );

  if (useModal) {
    return (
      <Modal open={Boolean(props.showDatePicker)} onClose={closeDatePicker} disableAutoFocus>
        <Box sx={dateRangeModalStyles}><div ref={datePickerRef}>{pickerContent}</div></Box>
      </Modal>
    );
  }

  if (usePopover) {
    return <Box sx={dateRangePopoverStyles} ref={datePickerRef}>{pickerContent}</Box>;
  }

  return <div style={dateRangePickerStyles} ref={datePickerRef}>{pickerContent}</div>;
};

const dateRangePickerStyles = { position: "absolute", zIndex: 80, backgroundColor: "white", marginLeft: "-160px", marginTop: 0, boxShadow: "2px 2px 8px 0px rgba(0,0,0,0.30)" };
const dateRangeModalStyles = { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "#fff", borderRadius: "12px", boxShadow: 24, p: "12px", width: "fit-content", maxWidth: "95vw", maxHeight: "95vh", overflow: "auto" };
const dateRangePopoverStyles = { bgcolor: "#fff", display: 'flex', flexDirection: 'column' };

export default SingleDatePicker;
